import { Trans, useLingui } from "@lingui/react/macro";
import type { CompanyProviderView } from "@rakazo/contracts";
import { Button, Input, Spinner, Textarea } from "@rakazo/ui-web";
import { useMutation } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { errorText } from "../../lib/user-error";
import { drokRpc } from "../rpc";

export const DEFAULT_CONTEXT_WINDOW = 128_000;
export const DEFAULT_MAX_TOKENS = 8_192;

export type ModelDraft = {
  id: string;
  name: string;
  included: boolean;
  contextWindow: string;
  maxTokens: string;
  vision: boolean;
  reasoning: boolean;
};

export type FormValues = {
  name: string;
  slug: string;
  baseUrl: string;
  apiKey: string;
  headers: string;
  enabled: boolean;
  models: ModelDraft[];
};

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export function emptyForm(): FormValues {
  return { name: "", slug: "", baseUrl: "", apiKey: "", headers: "", enabled: true, models: [] };
}

export function formFromProvider(provider: CompanyProviderView): FormValues {
  return {
    name: provider.name,
    slug: provider.slug,
    baseUrl: provider.baseUrl,
    apiKey: "",
    headers: "",
    enabled: provider.enabled,
    models: provider.models.map((model) => ({
      id: model.id,
      name: model.name ?? "",
      included: true,
      contextWindow: String(model.contextWindow),
      maxTokens: String(model.maxTokens),
      vision: model.vision === true,
      reasoning: model.reasoning === true,
    })),
  };
}

export function newModelDraft(id: string): ModelDraft {
  return {
    id,
    name: "",
    included: true,
    contextWindow: String(DEFAULT_CONTEXT_WINDOW),
    maxTokens: String(DEFAULT_MAX_TOKENS),
    vision: false,
    reasoning: false,
  };
}

/** One "Header-Name: value" per line. Returns undefined for an invalid line. */
export function parseHeaders(text: string): Record<string, string> | undefined {
  const headers: Record<string, string> = {};
  for (const line of text.split("\n")) {
    if (!line.trim()) continue;
    const at = line.indexOf(":");
    if (at <= 0) return undefined;
    headers[line.slice(0, at).trim()] = line.slice(at + 1).trim();
  }
  return headers;
}

export function toModels(drafts: ModelDraft[]) {
  return drafts
    .filter((draft) => draft.included)
    .map((draft) => ({
      id: draft.id.trim(),
      ...(draft.name.trim() ? { name: draft.name.trim() } : {}),
      contextWindow: Number(draft.contextWindow),
      maxTokens: Number(draft.maxTokens),
      ...(draft.vision ? { vision: true } : {}),
      ...(draft.reasoning ? { reasoning: true } : {}),
    }));
}

function positive(value: string): boolean {
  const n = Number(value);
  return Number.isSafeInteger(n) && n > 0;
}

/** Local checks that mirror the server's zod rules, so most mistakes show before a round trip. */
export function validateForm(
  values: FormValues,
  creating: boolean,
  keySet: boolean,
): string | null {
  if (!values.name.trim()) return "name";
  if (creating && !SLUG.test(values.slug)) return "slug";
  try {
    const url = new URL(values.baseUrl.trim());
    if ((url.protocol !== "http:" && url.protocol !== "https:") || url.username || url.password)
      return "url";
  } catch {
    return "url";
  }
  if (!keySet && !values.apiKey.trim()) return "key";
  if (values.headers.trim() && !parseHeaders(values.headers)) return "headers";
  const included = values.models.filter((model) => model.included);
  if (included.some((model) => !model.id.trim())) return "model-id";
  if (new Set(included.map((model) => model.id.trim())).size !== included.length) return "dup";
  if (included.some((m) => !positive(m.contextWindow) || !positive(m.maxTokens))) return "limits";
  if (included.some((m) => Number(m.maxTokens) >= Number(m.contextWindow))) return "max";
  return null;
}

export function CompanyProviderForm({
  provider,
  saving,
  error,
  onSave,
  onCancel,
}: {
  provider: CompanyProviderView | null;
  saving: boolean;
  error: string | null;
  onSave: (values: FormValues) => void;
  onCancel: () => void;
}) {
  const { t } = useLingui();
  const creating = provider === null;
  const ids = useId();
  const [values, setValues] = useState<FormValues>(() =>
    provider ? formFromProvider(provider) : emptyForm(),
  );
  const [manualId, setManualId] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);
  const keySet = provider?.apiKeySet === true && provider.unreadable !== true;
  const set = <K extends keyof FormValues>(key: K, value: FormValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const patchModel = (index: number, patch: Partial<ModelDraft>) =>
    set(
      "models",
      values.models.map((model, i) => (i === index ? { ...model, ...patch } : model)),
    );

  const probe = useMutation({
    mutationFn: () => {
      const headers = values.headers.trim() ? parseHeaders(values.headers) : undefined;
      return drokRpc.companyModels.probe({
        baseUrl: values.baseUrl.trim(),
        ...(values.apiKey.trim() ? { apiKey: values.apiKey.trim() } : {}),
        ...(!values.apiKey.trim() && provider ? { providerId: provider.id } : {}),
        ...(headers ? { headers } : {}),
      });
    },
    onSuccess: ({ models }) => {
      const known = new Set(values.models.map((model) => model.id));
      set("models", [
        ...values.models,
        ...models.filter((id) => !known.has(id)).map(newModelDraft),
      ]);
    },
  });

  const addManual = () => {
    const id = manualId.trim();
    if (!id || values.models.some((model) => model.id === id)) return;
    set("models", [...values.models, newModelDraft(id)]);
    setManualId("");
  };

  const messages: Record<string, string> = {
    name: t`Enter a name.`,
    slug: t`The slug may use lowercase letters, digits and single hyphens.`,
    url: t`Enter an http or https base URL without a username or password.`,
    key: t`Enter the API key.`,
    headers: t`Write each header as Name: value, one per line.`,
    "model-id": t`Every included model needs an id.`,
    dup: t`Model ids must be unique.`,
    limits: t`Context window and max tokens must be positive whole numbers.`,
    max: t`Max tokens must be smaller than the context window.`,
  };
  const submit = () => {
    const found = validateForm(values, creating, keySet);
    setProblem(found);
    if (!found) onSave(values);
  };
  const shown = problem ? messages[problem] : (error ?? null);

  return (
    <form
      aria-label={creating ? t`Add provider` : t`Edit provider`}
      className="space-y-4 rounded-xl border border-border p-4"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor={`${ids}-name`} className="text-sm font-medium">
            <Trans>Name</Trans>
          </label>
          <Input
            id={`${ids}-name`}
            ref={firstField}
            autoFocus
            maxLength={100}
            value={values.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>
        {creating ? (
          <div className="space-y-1">
            <label htmlFor={`${ids}-slug`} className="text-sm font-medium">
              <Trans>Slug</Trans>
            </label>
            <Input
              id={`${ids}-slug`}
              maxLength={40}
              value={values.slug}
              onChange={(e) => set("slug", e.target.value.toLowerCase())}
              aria-describedby={`${ids}-slug-help`}
            />
            <p id={`${ids}-slug-help`} className="text-xs text-muted-foreground">
              <Trans>Part of every model id. It cannot change later.</Trans>
            </p>
          </div>
        ) : null}
        <div className="space-y-1 sm:col-span-2">
          <label htmlFor={`${ids}-url`} className="text-sm font-medium">
            <Trans>Base URL</Trans>
          </label>
          <Input
            id={`${ids}-url`}
            type="url"
            placeholder="https://api.example.com/v1"
            value={values.baseUrl}
            onChange={(e) => set("baseUrl", e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${ids}-key`} className="text-sm font-medium">
            <Trans>API key</Trans>
          </label>
          <Input
            id={`${ids}-key`}
            type="password"
            autoComplete="new-password"
            placeholder={keySet ? t`Set (leave blank to keep)` : ""}
            value={values.apiKey}
            onChange={(e) => set("apiKey", e.target.value)}
          />
          {keySet ? (
            <p className="text-xs text-muted-foreground">
              <Trans>A key is stored. It is never shown again.</Trans>
            </p>
          ) : null}
        </div>
        <div className="space-y-1">
          <label htmlFor={`${ids}-headers`} className="text-sm font-medium">
            <Trans>Extra headers (optional)</Trans>
          </label>
          <p className="text-xs text-muted-foreground">
            <Trans>
              Authorization, Host, Cookie, Content-Length, Transfer-Encoding, Connection, Upgrade
              and Proxy-* headers are not allowed. The API key field handles authorization.
            </Trans>
          </p>
          <Textarea
            id={`${ids}-headers`}
            rows={2}
            placeholder={
              provider?.headerNames.length
                ? t`Leave blank to keep the stored headers`
                : "X-Team: finance"
            }
            value={values.headers}
            onChange={(e) => set("headers", e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={probe.isPending || !values.baseUrl.trim() || (!values.apiKey.trim() && !keySet)}
          onClick={() => probe.mutate()}
        >
          {probe.isPending ? <Spinner aria-label={t`Working…`} /> : null}
          <Trans>Fetch models</Trans>
        </Button>
        <Input
          aria-label={t`Add a model id by hand`}
          placeholder={t`Add a model id by hand`}
          className="max-w-64"
          value={manualId}
          onChange={(e) => setManualId(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addManual();
            }
          }}
        />
        <Button type="button" variant="outline" size="sm" onClick={addManual}>
          <Trans>Add model</Trans>
        </Button>
      </div>
      {probe.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {errorText(probe.error)}
        </p>
      ) : null}

      {values.models.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground">
                <th className="p-1">
                  <Trans>Include</Trans>
                </th>
                <th className="p-1">
                  <Trans>Model id</Trans>
                </th>
                <th className="p-1">
                  <Trans>Display name</Trans>
                </th>
                <th className="p-1">
                  <Trans>Context window</Trans>
                </th>
                <th className="p-1">
                  <Trans>Max tokens</Trans>
                </th>
                <th className="p-1">
                  <Trans>Vision</Trans>
                </th>
                <th className="p-1">
                  <Trans>Reasoning</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {values.models.map((model, index) => (
                <tr key={model.id} data-testid="company-model-row">
                  <td className="p-1">
                    <input
                      type="checkbox"
                      aria-label={t`Include ${model.id}`}
                      checked={model.included}
                      onChange={(e) => patchModel(index, { included: e.target.checked })}
                    />
                  </td>
                  <td className="p-1 font-mono text-xs">{model.id}</td>
                  <td className="p-1">
                    <Input
                      aria-label={t`Display name for ${model.id}`}
                      value={model.name}
                      onChange={(e) => patchModel(index, { name: e.target.value })}
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      aria-label={t`Context window for ${model.id}`}
                      inputMode="numeric"
                      value={model.contextWindow}
                      onChange={(e) => patchModel(index, { contextWindow: e.target.value })}
                    />
                  </td>
                  <td className="p-1">
                    <Input
                      aria-label={t`Max tokens for ${model.id}`}
                      inputMode="numeric"
                      value={model.maxTokens}
                      onChange={(e) => patchModel(index, { maxTokens: e.target.value })}
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="checkbox"
                      aria-label={t`Vision for ${model.id}`}
                      checked={model.vision}
                      onChange={(e) => patchModel(index, { vision: e.target.checked })}
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="checkbox"
                      aria-label={t`Reasoning for ${model.id}`}
                      checked={model.reasoning}
                      onChange={(e) => patchModel(index, { reasoning: e.target.checked })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          <Trans>No models yet. Fetch them from the server or add an id by hand.</Trans>
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={values.enabled}
          onChange={(e) => set("enabled", e.target.checked)}
        />
        <Trans>Enabled</Trans>
      </label>

      {shown ? (
        <p role="alert" className="text-sm text-destructive">
          {shown}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" disabled={saving} onClick={onCancel}>
          <Trans>Cancel</Trans>
        </Button>
        <Button type="submit" disabled={saving}>
          {saving ? <Spinner aria-label={t`Working…`} /> : null}
          <Trans>Save</Trans>
        </Button>
      </div>
    </form>
  );
}
